import React, { useContext } from 'react'

import pickBy from 'lodash/pickBy'
import NextLink from 'next/link'

import MapContext from '@/context/map'
import type { ComposedListing, ListingsGroup, ProductionDetail } from '@/types/app-types'
import { useMobileMediaQuery } from '@/utils'
import { trackSelectListing } from '@/utils/analytics'

import { QuantityFilterState } from '../context/types'
import ListingRow from '../listing-row'
import ListingsGroupRow from '../listings-group-row'

export interface ListingComponentProps {
    id: number | string
    listing: ComposedListing | ListingsGroup
    pathname: string
    production: ProductionDetail | undefined
    showUsd: boolean
    slug: string | string[] | undefined
    quantityFilterState: QuantityFilterState
    highlightSection: (sectionId: string) => void
    isDesktop: number | boolean
}

const ListingComponent: React.FC<ListingComponentProps> = ({
    listing,
    pathname,
    production,
    showUsd,
    slug,
    quantityFilterState,
    highlightSection,
    isDesktop,
}) => {
    const ListingRowComponent = listing.isGroup ? ListingsGroupRow : ListingRow

    const { isGroup, bestListingsByQuantity = {} } = listing as ListingsGroup
    const bestListing =
        isGroup && bestListingsByQuantity[quantityFilterState.quantity]
            ? bestListingsByQuantity[quantityFilterState.quantity]
            : listing
    const {
        selectors: {
            venueMapState: { onMapSectionClickFromListing },
            isMapboxMapEnabled,
        },
    } = useContext(MapContext)
    const isMobile = useMobileMediaQuery()
    return (
        <NextLink
            key={bestListing.id}
            href={{
                pathname,
                query: {
                    id: production?.id,
                    showDetails: bestListing.id,
                    groupId: listing.isGroup ? listing.id : undefined,
                    qty: bestListing.defaultQuantity,
                    ...pickBy({ slug }),
                },
            }}
            shallow
        >
            <ListingRowComponent
                bestListing={bestListing}
                showUsd={showUsd}
                {...(listing as ListingsGroup)}
                onClick={() => {
                    if (!!production) {
                        trackSelectListing(production, bestListing, quantityFilterState.quantity)
                        if (isMapboxMapEnabled && !isMobile)
                            onMapSectionClickFromListing?.(listing.sectionId ?? '', listing.mbi ?? '')
                    }
                }}
                onMouseEnter={() => isDesktop && highlightSection(bestListing.sectionId)}
            />
        </NextLink>
    )
}

export default ListingComponent
